import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    CatalogRatioShortData,
    CatalogUerBaseCostDetailedData,
    CatalogUerBaseCostEstimateBasisSectionCreateInput,
    UerEstimateBaseCostData,
} from 'gql/graphql';
import { WorkInSumEnum } from 'gql/graphql';
import { catalogUpdateUerBaseCostGql } from 'pages/Catalogs/CatalogUERBaseCost/catalogUerBaseCostQueries';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { catalogCreateUerBaseCostGql } from '../createOrEditUerQueries';

interface IUseSaveUerEstimate {
    onClose: () => void;
    watchForm: Record<string, any>;
    checkedIdCheckboxes: number[];
    catalogId?: number;
    parsedUerData?: UerEstimateBaseCostData | null;
    resetModalDataSaveData: () => void;
    refreshData: () => void;
    isEdit: boolean;
    rowData: CatalogUerBaseCostDetailedData | undefined | null;
}

export const useSaveUerEstimate = (props: IUseSaveUerEstimate) => {
    const {
        onClose,
        catalogId,
        watchForm,
        checkedIdCheckboxes,
        parsedUerData,
        resetModalDataSaveData,
        refreshData,
        isEdit,
        rowData,
    } = props;
    const dispatch = useDispatch();
    const [isSubmittedErr, setSubmittedErr] = useState<boolean>(false);

    const [resetCreateUerRowHandler] = useResetStoreData({ requestId: 'catalogCreateUerBaseCost' });
    const [resetUpdateUerRowHandler] = useResetStoreData({ requestId: 'catalogUpdateUerBaseCost' });

    const createOrUpdateSuccess = useCallback(() => {
        resetModalDataSaveData();
        refreshData();
    }, [refreshData, resetModalDataSaveData]);

    const [createUerBaseCostData, createUerBaseCostStatus, createUerBaseCostError, createUerBaseCostRequest] =
        useRequest({
            requestId: 'catalogCreateUerBaseCost',
            query: catalogCreateUerBaseCostGql,
            withEffect: false,
            dependenciesUpdater: createOrUpdateSuccess,
        });

    const [editUerBaseCostData, editUerBaseCostStatus, editUerBaseCostError, editUerBaseCostRequest] = useRequest({
        requestId: 'catalogUpdateUerBaseCost',
        query: catalogUpdateUerBaseCostGql,
        withEffect: false,
        dependenciesUpdater: createOrUpdateSuccess,
    });

    const {
        chapter,
        code,
        groupName,
        name,
        sectionName,
        uerClassifier,
        unit,
        unitCost,
        workCode,
        workInSum,
        workKind,
        workUnit,
        workRatio,
        ratios,
        ratioGroupsToComposeRatios,
        justification,
    } = watchForm || {};

    const costData = useMemo(() => {
        const dataSource = parsedUerData?.baseCost || rowData;
        const version = parsedUerData?.version || rowData?.version;

        const { emm, eqp, mtr, nr, ozp, pir, sp, trn, zpm, fot, directCost, tz, tm, sections = [] } = dataSource || {};

        const { workTypeItems, uerNacl, uerPlan, catFile, workID } = parsedUerData || {};

        const rowBaseCostWorkScopes = (rowData?.catalogUERBaseCostWorkScopes || []).map(
            (item: { name: string; number: number }) => ({
                name: item?.name,
                number: item?.number,
            }),
        );

        const updateWorkRatio =
            typeof workRatio === 'number' ? workRatio : Number(workRatio?.replace(/\s/g, '')?.replace(',', '.'));

        const flatRatioGroups =
            Array.from(
                new Set(
                    ratios
                        ?.filter(Boolean)
                        .map((ratio: CatalogRatioShortData) => ratio.catalogRatioGroupId)
                        .filter(Boolean),
                ).values(),
            ) ?? [];

        const flatRatios =
            ratios
                ?.filter(Boolean)
                ?.filter((ratio: CatalogRatioShortData) => !ratio?.catalogRatioGroupId)
                ?.map((ratio: CatalogRatioShortData) => ratio?.id) ?? [];

        return {
            code,
            emm,
            catalogId,
            chapter: chapter?.id,
            eqp,
            zpm,
            fot,
            directCost,
            tz,
            tm,
            catalogUERBaseCostWorkScopes: parsedUerData?.general?.workScopes || rowBaseCostWorkScopes,
            version,
            workType: {
                workUnit: workUnit || '',
                workRatio: updateWorkRatio,
                workName: workKind?.label,
                workCode: workCode || '',
                workInSum: workInSum?.id || WorkInSumEnum.NotParticipate,
                workUniqueId: workKind?.id,
            },
            unitCost,
            unit,
            uerClassifier: uerClassifier?.id,
            trn,
            sp,
            pir,
            name,
            mtr,
            groupName,
            nr,
            ozp,
            constructionTypes: checkedIdCheckboxes,
            sections: sections as Array<CatalogUerBaseCostEstimateBasisSectionCreateInput>,
            ratios: flatRatios,
            ratioGroups: flatRatioGroups,
            ratioGroupsToComposeRatios,
            catFile,
            workID,
            workTypeItems,
            uerNacl,
            uerPlan,
        };
    }, [
        parsedUerData,
        rowData,
        workRatio,
        ratios,
        code,
        catalogId,
        chapter?.id,
        workUnit,
        workKind?.label,
        workKind?.id,
        workCode,
        workInSum?.id,
        unitCost,
        unit,
        uerClassifier?.id,
        name,
        groupName,
        checkedIdCheckboxes,
        ratioGroupsToComposeRatios,
    ]);

    useEffect(() => {
        if (
            isSubmittedErr &&
            (!isEdit
                ? createUerBaseCostStatus === RequestStatus.SUCCESS &&
                  createUerBaseCostData?.catalogCreateUerBaseCost?.result
                : editUerBaseCostStatus === RequestStatus.SUCCESS &&
                  editUerBaseCostData?.catalogUpdateUerBaseCost?.result)
        ) {
            setSubmittedErr(false);
            onClose();
        }
    }, [
        createUerBaseCostData?.catalogCreateUerBaseCost?.result,
        createUerBaseCostError,
        createUerBaseCostStatus,
        editUerBaseCostData?.catalogUpdateUerBaseCost?.result,
        editUerBaseCostStatus,
        isEdit,
        isSubmittedErr,
        onClose,
    ]);

    const onAddOrEdit = useCallback(() => {
        dispatch(
            !isEdit
                ? createUerBaseCostRequest({
                      costData: { ...costData, groupCode: '', sectionName, justification },
                  })
                : editUerBaseCostRequest({
                      costData: { ...costData, id: rowData?.id, justification },
                  }),
        );
        setSubmittedErr(true);
    }, [
        dispatch,
        isEdit,
        createUerBaseCostRequest,
        costData,
        sectionName,
        justification,
        editUerBaseCostRequest,
        rowData?.id,
    ]);

    const handleCloseModalError = useCallback(() => {
        setSubmittedErr(false);
    }, []);

    return {
        onSubmit: onAddOrEdit,
        saveStatus: isEdit ? editUerBaseCostStatus : createUerBaseCostStatus,
        saveDataReset: isEdit ? resetUpdateUerRowHandler : resetCreateUerRowHandler,
        isSubmittedErr,
        dataRequestErr: isEdit
            ? editUerBaseCostData?.catalogUpdateUerBaseCost
            : createUerBaseCostData?.catalogCreateUerBaseCost,
        currentError: isEdit ? editUerBaseCostError : createUerBaseCostError,
        handleCloseModalError,
    };
};
